import {
  negQ,
  type QuadNumber,
  quadFromRational,
} from "@/lib/tool-kit/linear-algebra/quad-number";
import { BIG_ONE, gcdBig } from "@/lib/tool-kit/math/bigint";
import { equalsR, intR, makeRational } from "@/lib/tool-kit/math/rational";

/**
 * Exact sines and cosines of the angles every course memorises: multiples of 30° and 45°.
 *
 * Values are numbers in Q(√d), so cos 30° is √3/2 exactly and a vector of length 10 at
 * 30° has components 5√3 and 5 rather than 8.660254…
 */

const half = makeRational(BigInt(1), BigInt(2));
const sqrt3Over2: QuadNumber = { a: intR(0), b: half, d: BigInt(3) };
const sqrt2Over2: QuadNumber = { a: intR(0), b: half, d: BigInt(2) };
const ZERO = quadFromRational(intR(0));
const ONE = quadFromRational(intR(1));
const HALF = quadFromRational(half);

/** [cos, sin] for the reference angles in the first quadrant. */
const FIRST_QUADRANT: Record<number, [QuadNumber, QuadNumber]> = {
  0: [ONE, ZERO],
  30: [sqrt3Over2, HALF],
  45: [sqrt2Over2, sqrt2Over2],
  60: [HALF, sqrt3Over2],
};

export interface ExactTrig {
  cos: QuadNumber;
  sin: QuadNumber;
}

export function equalsQuad(x: QuadNumber, y: QuadNumber): boolean {
  return (
    equalsR(x.a, y.a) &&
    equalsR(x.b, y.b) &&
    (x.d === y.d || equalsR(x.b, intR(0)))
  );
}

/** Exact cos and sin of an integer number of degrees that is a multiple of 30 or 45. */
export function trigOfDegrees(degrees: number): ExactTrig | null {
  if (!Number.isInteger(degrees)) {
    return null;
  }
  const angle = ((degrees % 360) + 360) % 360;
  if (angle % 30 !== 0 && angle % 45 !== 0) {
    return null;
  }
  const quadrant = Math.floor(angle / 90);
  const reference = angle - 90 * quadrant;
  const base = FIRST_QUADRANT[reference];
  if (!base) {
    return null;
  }
  const [c, s] = base;
  switch (quadrant) {
    case 0:
      return { cos: c, sin: s };
    case 1:
      return { cos: negQ(s), sin: c };
    case 2:
      return { cos: negQ(c), sin: negQ(s) };
    default:
      return { cos: s, sin: negQ(c) };
  }
}

const SPECIAL_ANGLES = [0, 30, 45, 60, 90, 120, 135, 150, 180] as const;

/** The special angle in [0°, 180°] whose cosine is exactly `cos`, if there is one. */
export function angleFromCos(cos: QuadNumber): number | null {
  for (const degrees of SPECIAL_ANGLES) {
    const trig = trigOfDegrees(degrees);
    if (trig && equalsQuad(trig.cos, cos)) {
      return degrees;
    }
  }
  return null;
}

/** An angle in degrees as a multiple of π: 150 → "5π/6", 180 → "π", 0 → "0". */
export function piMultiple(degrees: number): string {
  if (degrees === 0) {
    return "0";
  }
  const sign = degrees < 0 ? "−" : "";
  const n = BigInt(Math.abs(degrees));
  const d = BigInt(180);
  const g = gcdBig(n, d);
  const top = n / g;
  const bottom = d / g;
  const lead = top === BIG_ONE ? "π" : `${top}π`;
  return bottom === BIG_ONE ? `${sign}${lead}` : `${sign}${lead}/${bottom}`;
}
